// @ts-nocheck
import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import { supabaseClient } from "@/lib/supabase/client"
import { useToast } from "@/components/ui/ToastProvider"

export default function RegisterPage() {
  const navigate = useNavigate()
  const { error: showErr, success } = useToast()
  const [step, setStep] = useState<"form" | "otp">("form")
  const [form, setForm] = useState({ email: "", username: "", display_name: "", password: "" })
  const [errs, setErrs] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [otpCode, setOtpCode] = useState("")
  const [checkingUsername, setCheckingUsername] = useState(false)
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [k]: e.target.value }))

  const checkUsernameUnique = async (username: string): Promise<boolean> => {
    const { data } = await supabaseClient.from("users").select("id").eq("username", username).maybeSingle()
    return !data // true = available
  }

  const validate = async () => {
    const e: Record<string, string> = {}
    if (!form.email || !/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) e.email = "Valid email required"
    if (!form.username || form.username.length < 3) e.username = "Min 3 characters"
    else if (!/^[a-z0-9_]+$/.test(form.username)) e.username = "Lowercase letters, numbers and _ only"
    else {
      setCheckingUsername(true)
      const available = await checkUsernameUnique(form.username)
      setCheckingUsername(false)
      if (!available) e.username = "Username is already taken"
    }
    if (!form.display_name || form.display_name.length < 2) e.display_name = "Min 2 characters"
    if (!form.password || form.password.length < 8) e.password = "Min 8 characters"
    setErrs(e)
    return !Object.keys(e).length
  }

  const handleSignUp = async (ev: React.FormEvent) => {
    ev.preventDefault()
    const ok = await validate()
    if (!ok) return
    setLoading(true)
    try {
      const { error } = await supabaseClient.auth.signUp({
        email: form.email,
        password: form.password,
        options: { data: { username: form.username, display_name: form.display_name } }
      })
      if (error) { showErr("Sign up failed", error.message); return }
      setOtpCode("")
      setStep("otp")
    } catch (e: any) {
      showErr("Sign up failed", e.message)
    } finally { setLoading(false) }
  }

  const handleOTPVerify = async (ev: React.FormEvent) => {
    ev.preventDefault()
    const token = otpCode.replace(/\s/g, "").trim()
    if (token.length < 6) { showErr("Invalid code", "Enter the full OTP code"); return }
    setLoading(true)
    try {
      const { data, error } = await supabaseClient.auth.verifyOtp({ email: form.email, token, type: "signup" })
      if (error) { showErr("Invalid code", error.message); return }
      if (data.session) { success("Welcome to SQUIDS!"); navigate("/feed") }
      else { success("Email verified! Please log in."); navigate("/auth/login") }
    } catch (e: any) {
      showErr("Verify failed", e.message)
    } finally { setLoading(false) }
  }

  return (
    <div className="auth-wrap">
      <div className="auth-side-left hidden lg:flex flex-col">
        <div className="flex items-center gap-3 mb-16"><SquidsIcon size={36} /><span className="sq-logo text-3xl">SQUIDS</span></div>
        <div className="flex-1 flex flex-col justify-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .2 }}>
            <h2 className="font-outfit font-black text-4xl xl:text-5xl leading-tight mb-6" style={{ color: "var(--text)" }}>
              Join Africa's<br />conversation<br /><span className="text-brand">platform.</span>
            </h2>
            <div className="space-y-4 mt-8">
              {["Ask anything — no judgment", "Get answers from real people", "Build your community"].map((t, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: .3 + i * .1 }} className="flex items-center gap-3">
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--brand)", flexShrink: 0 }} />
                  <span className="text-sm" style={{ color: "var(--text2)" }}>{t}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
        <div className="flex gap-4 text-xs" style={{ color: "var(--text3)" }}>
          <a href="https://policies.squids.co.za" target="_blank" rel="noreferrer" className="hover:text-brand">Terms</a>
          <a href="https://policies.squids.co.za" target="_blank" rel="noreferrer" className="hover:text-brand">Privacy</a>
        </div>
        <FloatingOrb />
      </div>

      <div className="auth-side-right flex-1">
        <motion.div className="auth-box" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-2 mb-5 lg:hidden"><SquidsIcon size={26} /><span className="sq-logo text-2xl">SQUIDS</span></div>

          <AnimatePresence mode="wait">
            {step === "form" && (
              <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <h1 className="font-outfit font-bold text-xl mb-1">Create your account</h1>
                <p className="text-sm mb-5" style={{ color: "var(--text2)" }}>It's free and always will be.</p>
                <form onSubmit={handleSignUp} className="space-y-3">
                  {([
                    { k: "email", label: "Email", type: "email", ph: "you@example.com" },
                    { k: "username", label: "Username", type: "text", ph: "lowercase_only", hint: "Letters, numbers and _ only · must be unique" },
                    { k: "display_name", label: "Display name", type: "text", ph: "How others see you" },
                    { k: "password", label: "Password", type: "password", ph: "8+ characters" },
                  ] as any[]).map(({ k, label, type, ph, hint }: any) => (
                    <div key={k}>
                      <label className="text-xs font-bold uppercase tracking-wider block mb-1" style={{ color: "var(--text3)" }}>{label}</label>
                      <input type={type} value={form[k as keyof typeof form]} onChange={set(k)} placeholder={ph} className="sq-input"
                        autoComplete={type === "email" ? "email" : type === "password" ? "new-password" : "off"} />
                      {hint && !errs[k] && <p className="text-xs mt-1" style={{ color: "var(--text3)" }}>{hint}</p>}
                      {errs[k] && <p className="text-xs mt-1" style={{ color: "var(--danger)" }}>{errs[k]}</p>}
                    </div>
                  ))}
                  <button type="submit" disabled={loading || checkingUsername} className="sq-btn sq-btn-primary w-full py-2.5">
                    {loading || checkingUsername ? <Spinner /> : "Create Account"}
                  </button>
                </form>
                <p className="text-center text-xs mt-4" style={{ color: "var(--text3)" }}>
                  By signing up you agree to our <a href="https://policies.squids.co.za" target="_blank" rel="noreferrer" className="hover:underline text-brand">Terms</a> &amp;{" "}
                  <a href="https://policies.squids.co.za" target="_blank" rel="noreferrer" className="hover:underline text-brand">Privacy Policy</a>.
                </p>
              </motion.div>
            )}
            {step === "otp" && (
              <motion.div key="otp" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <h1 className="font-outfit font-bold text-xl mb-2">Verify your email</h1>
                <p className="text-sm mb-1" style={{ color: "var(--text2)" }}>We sent a code to <strong>{form.email}</strong></p>
                <p className="text-xs mb-5" style={{ color: "var(--text3)" }}>Check your inbox for a 6 or 8 digit verification code.</p>
                <form onSubmit={handleOTPVerify} className="space-y-3">
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider block mb-1" style={{ color: "var(--text3)" }}>Verification code</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={otpCode}
                      onChange={e => setOtpCode(e.target.value.replace(/[^0-9]/g, ""))}
                      placeholder="Enter your code"
                      className="sq-input"
                      maxLength={8}
                      autoFocus
                      autoComplete="one-time-code"
                      style={{ letterSpacing: ".2em", fontSize: "1.1rem", textAlign: "center" }}
                    />
                    <p className="text-xs mt-1" style={{ color: "var(--text3)" }}>Paste or type your 6 or 8 digit code</p>
                  </div>
                  <button type="submit" disabled={loading || otpCode.length < 6} className="sq-btn sq-btn-primary w-full py-2.5">
                    {loading ? <Spinner /> : "Verify Email"}
                  </button>
                </form>
                <button onClick={() => setStep("form")} className="mt-3 w-full text-center text-xs" style={{ color: "var(--text3)", background: "none", border: "none", cursor: "pointer" }}>
                  ← Back to registration
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="sq-panel mt-4 p-4 text-center">
            <span className="text-sm" style={{ color: "var(--text2)" }}>Already have an account? </span>
            <Link to="/auth/login" className="text-sm font-bold text-brand hover:underline">Log In</Link>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

function SquidsIcon({ size = 32 }: { size?: number }) {
  return (<svg width={size} height={size} viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="10" fill="#ff4500" /><path d="M15 17.5C15 14.462 17.462 12 20.5 12H29C30.105 12 31 12.895 31 14C31 15.105 30.105 16 29 16H20.5C19.672 16 19 16.672 19 17.5C19 18.328 19.672 19 20.5 19H27.5C30.538 19 33 21.462 33 24.5C33 27.538 30.538 30 27.5 30H19C17.895 30 17 30.895 17 32C17 33.105 17.895 34 19 34H28.5C29.328 34 30 34.672 30 35.5C30 36.328 29.328 37 28.5 37H19C16.239 37 14 34.761 14 32C14 29.239 16.239 27 19 27H27.5C28.328 27 29 26.328 29 25.5C29 24.672 28.328 24 27.5 24H20.5C17.462 24 15 21.538 15 18.5V17.5Z" fill="white" /></svg>)
}
function FloatingOrb() {
  return (<div style={{ position: "absolute", top: "30%", right: "-60px", width: "240px", height: "240px", borderRadius: "9999px", background: "radial-gradient(circle,rgba(255,69,0,.15) 0%,transparent 70%)", pointerEvents: "none", animation: "float 8s ease-in-out infinite" }} />)
}
function Spinner() {
  return (<svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity=".25" /><path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" /></svg>)
}
